import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { ListResponse, TeamMember } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { authorityLabel, isOwner as membershipIsOwner } from "../../auth/permissions";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { formatDate, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  ReasonDialog,
  StatusPill,
} from "../../ui/components";
import { PanelSkeleton } from "../../ui/skeletons";

interface OwnershipTransfer {
  id: string;
  tenantId: string;
  tenantName: string;
  fromMembershipId: string;
  fromUserId: string;
  fromName: string;
  fromEmail: string;
  toMembershipId: string;
  toUserId: string;
  toName: string;
  toEmail: string;
  status: string;
  reason: string;
  resolutionReason: string;
  initiatedAt: string;
  acceptedAt?: string;
  verifiedAt?: string;
  completedAt?: string;
  expiresAt: string;
  version: number;
}

type TransferAction = { transfer: OwnershipTransfer; action: "accept" | "reject" | "cancel" };

export function OwnershipSettings() {
  const auth = useAuth();
  const isOwner = membershipIsOwner(auth.session?.selectedMembership);
  const userId = auth.session?.user.id;
  const sensitive = useSensitiveAction({
    setupTitle: "Protect ownership changes with two-step verification",
    lockedTitle: "Confirm your identity to change the account owner",
    lockedDescription:
      "The account owner has the highest level of access. Enter a fresh code before starting or deciding a transfer.",
  });
  const transfers = useQuery({
    queryKey: ["ownership-transfers"],
    queryFn: () => api<ListResponse<OwnershipTransfer>>("/api/v1/ownership-transfers"),
  });
  const members = useQuery({
    queryKey: ["tenant", "members"],
    queryFn: () => api<ListResponse<TeamMember>>("/api/v1/members"),
    enabled: isOwner,
  });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [action, setAction] = useState<TransferAction>();

  async function initiate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    try {
      await api<OwnershipTransfer>("/api/v1/ownership-transfers", {
        method: "POST",
        body: jsonBody({
          targetMembershipId: String(form.get("targetMembershipId")),
          reason: String(form.get("reason")),
        }),
      });
      formElement.reset();
      await transfers.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function submitAction(reason: string) {
    if (!action) return;
    setWorking(true);
    setError(undefined);
    try {
      await api<OwnershipTransfer>(
        `/api/v1/ownership-transfers/${action.transfer.id}/${action.action}`,
        { method: "POST", body: jsonBody({ reason, version: action.transfer.version }) },
      );
      setAction(undefined);
      await transfers.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (transfers.isLoading || (isOwner && members.isLoading)) return <PanelSkeleton />;
  if (transfers.error || members.error)
    return <ErrorPanel error={transfers.error ?? members.error} />;
  const items = transfers.data?.items ?? [];
  // Ownership is standing in the organisation and moves whole, so anybody already owning either
  // half is not a candidate to receive it. Mirrors the target check in InitiateOwnershipTransfer.
  const candidates = (members.data?.items ?? []).filter(
    (member) =>
      member.membershipStatus === "active" &&
      member.eventRole !== "owner" &&
      member.bookingRole !== "owner",
  );
  const active = items.find((item) =>
    ["pending_acceptance", "pending_verification"].includes(item.status),
  );

  return (
    <div className="stack">
      <Notice tone="warning" title="Three-party ownership transfer">
        A transfer requires the current owner to initiate it, the incoming owner to accept it with
        recent MFA, and Pixel Apocalypse to verify it. No role changes occur before all three gates
        pass. Completed transfers revoke both users’ tenant sessions so their new authority is
        loaded safely.
      </Notice>
      {sensitive.view}
      {error ? <ErrorPanel error={error} /> : null}
      {isOwner ? (
        <Card>
          <CardHeader
            title="Start ownership transfer"
            description="Choose an existing active employee. The acceptance and platform-verification window lasts seven days."
          />
          {active ? (
            <Notice tone="info">
              A transfer is already active. Cancel it before starting another.
            </Notice>
          ) : null}
          <form className="form-grid" onSubmit={initiate}>
            <Field label="Incoming owner">
              <select name="targetMembershipId" required defaultValue="" disabled={Boolean(active)}>
                <option value="" disabled>
                  Select employee
                </option>
                {candidates.map(({ membershipId, displayName, email, eventRole, bookingRole }) => (
                  <option key={membershipId} value={membershipId}>
                    {displayName}, {email} ({authorityLabel(eventRole, bookingRole)})
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Business reason"
              hint="Record why this person is authorized to take legal and operational ownership."
            >
              <textarea
                name="reason"
                required
                minLength={10}
                maxLength={1000}
                disabled={Boolean(active)}
              />
            </Field>
            <div className="full">
              <FormActions
                submitting={working}
                disabled={!sensitive.unlocked || Boolean(active) || candidates.length === 0}
                submitLabel="Start controlled transfer"
              />
            </div>
          </form>
          {!candidates.length ? (
            <Notice tone="info">
              Invite and activate another employee before transferring ownership.
            </Notice>
          ) : null}
        </Card>
      ) : null}
      <Card>
        <CardHeader
          title="Ownership transfer history"
          description="Pending gates, expiry, and final decisions remain visible as an audit-friendly workflow record."
        />
        {items.length ? (
          <DataTable
            caption="Ownership transfers"
            items={items}
            getKey={(item) => item.id}
            columns={[
              {
                label: "From",
                render: (item) => (
                  <span>
                    <strong>{item.fromName}</strong>
                    <br />
                    <small>{item.fromEmail}</small>
                  </span>
                ),
              },
              {
                label: "To",
                render: (item) => (
                  <span>
                    <strong>{item.toName}</strong>
                    <br />
                    <small>{item.toEmail}</small>
                  </span>
                ),
              },
              { label: "Started", render: (item) => formatDate(item.initiatedAt) },
              { label: "Expires", render: (item) => formatDate(item.expiresAt) },
              { label: "Status", render: (item) => <StatusPill value={item.status} /> },
              {
                label: "Action",
                render: (item) => {
                  if (item.status === "pending_acceptance" && item.toUserId === userId)
                    return (
                      <span className="inline">
                        <button
                          className="button primary small"
                          type="button"
                          disabled={working || !sensitive.unlocked}
                          onClick={() => setAction({ transfer: item, action: "accept" })}
                        >
                          Accept
                        </button>
                        <button
                          className="button danger small"
                          type="button"
                          disabled={working || !sensitive.unlocked}
                          onClick={() => setAction({ transfer: item, action: "reject" })}
                        >
                          Reject
                        </button>
                      </span>
                    );
                  if (
                    ["pending_acceptance", "pending_verification"].includes(item.status) &&
                    item.fromUserId === userId
                  )
                    return (
                      <button
                        className="button danger small"
                        type="button"
                        disabled={working || !sensitive.unlocked}
                        onClick={() => setAction({ transfer: item, action: "cancel" })}
                      >
                        Cancel
                      </button>
                    );
                  return item.resolutionReason || "Not available";
                },
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No ownership transfers"
            description="The current owner remains unchanged."
          />
        )}
      </Card>
      <ReasonDialog
        open={Boolean(action)}
        onClose={() => setAction(undefined)}
        onSubmit={submitAction}
        title={`${titleCase(action?.action ?? "confirm")} ownership transfer`}
        description={
          action
            ? `${action.transfer.toName} is the proposed incoming owner. This decision is version-checked and permanently audited.`
            : undefined
        }
        initialReason={
          action?.action === "accept"
            ? "I accept the responsibilities of tenant ownership."
            : "Record the reason for this decision."
        }
        submitLabel={titleCase(action?.action ?? "confirm")}
        tone={action?.action === "accept" ? "primary" : "danger"}
        minLength={10}
        submitting={working}
        disabled={!sensitive.unlocked}
      />
    </div>
  );
}
